import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft2, Cup } from "iconsax-react";
import styled from "styled-components";

import MobileLayout from "../../components/MobileLayout";
import { Md3Button } from "../../shared/components";
import { useGamification } from "../../hooks/useGamification";
import { shuffle, type GameWord } from "../../hooks/useGameWords";
import {
  CompleteCard,
  Feedback,
  GamePageRoot,
  GameTitle,
  GameTopBar,
  ProgressText,
  ScoreBadge,
} from "./gameStyles";
import { fontSizes } from "../../shared/styles/typography";

type Props = { words: GameWord[]; onExit?: () => void };

const DotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 64px);
  gap: 16px;
  justify-content: center;
`;

const Dot = styled.button<{ $active?: boolean; $done?: boolean }>`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 3px solid
    ${({ $active, $done, theme }) => {
      if ($done) return theme.colors.success;
      if ($active) return theme.colors.primary;
      return theme.colors.border;
    }};
  background: ${({ $active, $done, theme }) => {
    if ($done) return "rgba(16,185,129,0.2)";
    if ($active) return theme.colors.primaryAlpha10;
    return theme.colors.surface;
  }};
  font-size: ${fontSizes.base}px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
`;

const ConnectDotsGame: React.FC<Props> = ({ words, onExit }) => {
  const navigate = useNavigate();
  const { addPoints } = useGamification();

  const rounds = useMemo(() => {
    return words.slice(0, 5).map((w) => {
      const letters = w.palavra
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^A-Za-z]/g, "")
        .toUpperCase()
        .slice(0, 6)
        .split("");
      const order = letters.map((_, i) => i + 1);
      const positions = shuffle(
        order.map((n, i) => ({ n, letter: letters[i] }))
      );
      return { word: w, positions, sequence: order };
    });
  }, [words]);

  const [index, setIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState<"ok" | "bad" | null>(null);
  const [done, setDone] = useState(false);

  const current = rounds[index];

  const handleDot = async (n: number) => {
    if (!current || feedback) return;
    const expected = current.sequence[progress];
    if (n === expected) {
      const next = progress + 1;
      if (next >= current.sequence.length) {
        setFeedback("ok");
        setScore((s) => s + 1);
        await addPoints("GAME_MEMORY");
      } else {
        setProgress(next);
      }
    } else {
      setFeedback("bad");
      setProgress(0);
    }
  };

  const handleNext = () => {
    if (index + 1 >= rounds.length) {
      setDone(true);
      return;
    }
    setIndex((i) => i + 1);
    setProgress(0);
    setFeedback(null);
  };

  return (
    <MobileLayout
      headerTitle="Ligar os Pontos"
      headerType="private"
      showBannerAd={false}
    >
      <GamePageRoot>
        <GameTopBar>
          <Md3Button
            title="Voltar"
            variant="text"
            onClick={() => (onExit ? onExit() : navigate("/games"))}
            icon={<ArrowLeft2 size={18} />}
          />
          <ScoreBadge>
            <Cup size={16} variant="Bold" />
            {score}
          </ScoreBadge>
        </GameTopBar>

        {done || !current ? (
          <CompleteCard>
            <GameTitle>{done ? "Concluído!" : "Sem rodadas"}</GameTitle>
            <Md3Button
              title="Voltar aos jogos"
              onClick={() => navigate("/games")}
            />
          </CompleteCard>
        ) : (
          <>
            <ProgressText>
              Toque na ordem das letras de:{" "}
              <strong>{current.word.palavra.replace(/\d+$/, "")}</strong>
            </ProgressText>
            <DotGrid>
              {current.positions.map((p) => (
                <Dot
                  key={`${p.n}-${p.letter}`}
                  type="button"
                  $active={current.sequence[progress] === p.n}
                  $done={feedback === "ok"}
                  onClick={() => handleDot(p.n)}
                >
                  {p.letter}
                </Dot>
              ))}
            </DotGrid>
            {feedback ? (
              <>
                <Feedback $ok={feedback === "ok"}>
                  {feedback === "ok"
                    ? "Sequência correta!"
                    : "Ordem errada — tente de novo"}
                </Feedback>
                {feedback === "ok" ? (
                  <Md3Button title="Próxima" onClick={handleNext} />
                ) : (
                  <Md3Button
                    title="Tentar novamente"
                    onClick={() => {
                      setFeedback(null);
                      setProgress(0);
                    }}
                  />
                )}
              </>
            ) : null}
          </>
        )}
      </GamePageRoot>
    </MobileLayout>
  );
};

export default ConnectDotsGame;
